import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Info, Loader2 } from "lucide-react";
import { api, money } from "../../lib/api";
import {
  AMOUNT_TOKEN, TRUST_BADGE_ICONS, TRUST_BADGE_PRESETS, fillAmount, hasAmountToken, trustBadgeIcon,
} from "../../lib/trustBadges";
import { getPreset } from "../../theme/presets";
import { Switch } from "../ui/switch";
import { apiErrorMessage } from "./PushScheduling";

/**
 * Admin editor for the trust / benefits strip at the foot of the storefront
 * home (Free Shipping · Premium Quality · Secure Payment).
 *
 * One card per section. Each card saves ONLY its own section, and the on/off
 * switch and the position arrows save just that one setting, so editing one
 * card can never disturb another — including edits still unsaved in it.
 * Defaults, the icon list and the limits all come from the server.
 */

const SECTION_NAMES = {
  free_shipping: "Free Shipping",
  premium_quality: "Premium Quality",
  secure_payment: "Secure Payment",
};
const CURRENCY_SYMBOL = money(0).replace(/[\d.,\s]/g, "");

const toDraft = (section) => ({
  title: section.custom.title || "",
  description: section.custom.description || "",
  icon: section.custom.icon || "",
  threshold: section.custom.threshold == null ? "" : String(section.custom.threshold),
});

/** Draft amount → number, `null` when blank (use the default), NaN when invalid. */
function parseThreshold(value, max) {
  if (value === "") return null;
  const n = Number(value);
  return /^\d+$/.test(value.trim()) && n <= max ? n : NaN;
}

/** What the storefront would show for this draft (blank fields → defaults). */
function effectiveOf(section, draft, limits) {
  const amount = parseThreshold(draft.threshold, limits.threshold_max);
  return {
    title: draft.title.trim() || section.defaults.title,
    description: draft.description.trim() || section.defaults.description,
    icon: draft.icon || section.defaults.icon,
    threshold: amount === null || Number.isNaN(amount) ? section.defaults.threshold : amount,
  };
}

function IconPicker({ sectionKey, icons, value, defaultIcon, onPick }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Icon" data-testid={`trust-icons-${sectionKey}`}>
      {icons.filter((k) => Object.prototype.hasOwnProperty.call(TRUST_BADGE_ICONS, k)).map((k) => {
        const { Icon, label } = TRUST_BADGE_ICONS[k];
        const on = value === k;
        return (
          <button key={k} type="button" aria-pressed={on} aria-label={label}
            title={k === defaultIcon ? `${label} (default)` : label} onClick={() => onPick(k)}
            className={`relative w-11 h-11 inline-flex items-center justify-center border transition-colors ${on ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))] text-white" : "border-[hsl(var(--kb-border))] bg-white hover:bg-[hsl(var(--kb-surface-alt))]"}`}
            data-testid={`trust-icon-${sectionKey}-${k}`}>
            <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

/** Drawn exactly like the storefront strip, so what you see is what ships. */
function BadgePreview({ sectionKey, effective, hasAmount, enabled }) {
  const Icon = trustBadgeIcon(effective.icon);
  const description = hasAmount ? fillAmount(effective.description, effective.threshold, money) : effective.description;
  return (
    <div className={`md:self-start border border-dashed border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] ${enabled ? "" : "opacity-50"}`}
      data-testid={`trust-preview-${sectionKey}`}>
      <div className="px-3 pt-2 text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">
        {enabled ? "Storefront preview" : "Hidden on the storefront"}
      </div>
      <div className="flex flex-col items-center text-center p-5 space-y-2 min-w-0">
        <Icon size={32} strokeWidth={1.5} className="text-[hsl(var(--kb-text))] shrink-0" aria-hidden="true" />
        <div className="max-w-full text-sm font-semibold text-[hsl(var(--kb-text))] uppercase tracking-wider break-words [overflow-wrap:anywhere]"
          data-testid={`trust-preview-title-${sectionKey}`}>{effective.title}</div>
        <div className="max-w-full text-sm text-[hsl(var(--kb-muted))] break-words [overflow-wrap:anywhere]"
          data-testid={`trust-preview-description-${sectionKey}`}>{description}</div>
      </div>
    </div>
  );
}

/** The amount field, plus the reminder that it is display-only. */
function ThresholdField({ sectionKey, draft, set, section, limits, invalid, checkoutThreshold, effective }) {
  const differs = checkoutThreshold != null && checkoutThreshold !== effective.threshold;
  return (
    <div>
      <label className="overline block mb-1" htmlFor={`trust-threshold-${sectionKey}`}>Free shipping amount shown to shoppers</label>
      <div className="flex items-stretch max-w-[14rem]">
        <span className="inline-flex items-center px-3 border border-r-0 border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] text-sm">{CURRENCY_SYMBOL}</span>
        <input id={`trust-threshold-${sectionKey}`} type="number" inputMode="numeric" min={0} max={limits.threshold_max} step={1}
          className="kb-input min-w-0" value={draft.threshold} placeholder={String(section.defaults.threshold)}
          aria-invalid={invalid} onChange={(e) => set("threshold", e.target.value)} data-testid={`trust-threshold-${sectionKey}`} />
      </div>
      {invalid && (
        <div className="text-xs text-red-600 mt-1" role="alert" data-testid={`trust-threshold-error-${sectionKey}`}>
          Enter a whole amount between 0 and {money(limits.threshold_max)}, without paise.
        </div>
      )}
      <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1 leading-relaxed" data-testid={`trust-display-only-${sectionKey}`}>
        Display only. This is the amount written on your storefront — it does not change what checkout charges.
        Shipping charges are set under <Link to="/admin/loyalty" className="underline">Loyalty → Free Shipping Threshold</Link>.
      </p>
      {differs && (
        <div className="text-xs text-amber-700 mt-1 leading-relaxed" data-testid={`trust-checkout-notice-${sectionKey}`}>
          Heads up: checkout currently gives free shipping from {money(checkoutThreshold)}, but shoppers will be told {money(effective.threshold)}.
        </div>
      )}
    </div>
  );
}

function BadgeCard({ section, draft, onDraft, position, count, icons, limits, busy, checkoutThreshold, onToggle, onMove, onSave }) {
  const { key } = section;
  const set = (field, value) => onDraft({ ...draft, [field]: value });
  const hasAmount = "threshold" in section.defaults;
  const effective = effectiveOf(section, draft, limits);
  const amountInvalid = hasAmount && Number.isNaN(parseThreshold(draft.threshold, limits.threshold_max));
  const dirty = ["title", "description", "icon", ...(hasAmount ? ["threshold"] : [])].some((f) => effective[f] !== section[f]);
  const name = SECTION_NAMES[key] || section.defaults.title;
  // Append the token, shortening the text if needed so it is never cut in half.
  const insertAmount = () => set("description",
    `${draft.description.slice(0, limits.description - AMOUNT_TOKEN.length - 1).trimEnd()} ${AMOUNT_TOKEN}`);

  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4 sm:p-6" data-testid={`trust-card-${key}`} data-enabled={section.enabled}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-xl min-w-0">{name}</h3>
        <div className="flex items-center gap-2">
          <button type="button" className="kb-btn-outline !p-0 w-10 h-10 inline-flex items-center justify-center disabled:opacity-40"
            disabled={position === 0 || !!busy} onClick={() => onMove(-1)} aria-label={`Move ${name} earlier`} title="Show earlier"
            data-testid={`trust-move-up-${key}`}><ArrowUp size={16} /></button>
          <button type="button" className="kb-btn-outline !p-0 w-10 h-10 inline-flex items-center justify-center disabled:opacity-40"
            disabled={position === count - 1 || !!busy} onClick={() => onMove(1)} aria-label={`Move ${name} later`} title="Show later"
            data-testid={`trust-move-down-${key}`}><ArrowDown size={16} /></button>
          <label className="flex items-center gap-2 text-sm pl-2">
            <span data-testid={`trust-enabled-label-${key}`}>{section.enabled ? "Shown" : "Hidden"}</span>
            <Switch checked={section.enabled} disabled={!!busy} onCheckedChange={onToggle}
              aria-label={`Show ${name} on the storefront`} data-testid={`trust-enabled-${key}`} />
          </label>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="space-y-4 min-w-0">
          <div>
            <label className="overline block mb-1" htmlFor={`trust-title-${key}`}>Title</label>
            <input id={`trust-title-${key}`} className="kb-input" value={draft.title} placeholder={section.defaults.title}
              maxLength={limits.title} onChange={(e) => set("title", e.target.value)} data-testid={`trust-title-${key}`} />
          </div>
          <div>
            <label className="overline block mb-1" htmlFor={`trust-description-${key}`}>Description</label>
            <textarea id={`trust-description-${key}`} rows={2} className="kb-input" value={draft.description}
              placeholder={section.defaults.description} maxLength={limits.description}
              onChange={(e) => set("description", e.target.value)} data-testid={`trust-description-${key}`} />
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mt-1 text-[11px] text-[hsl(var(--kb-muted))]">
              <span>{hasAmount && <>Write <code>{AMOUNT_TOKEN}</code> where the amount should appear.</>}</span>
              <span>{draft.description.length}/{limits.description}</span>
            </div>
            {hasAmount && !hasAmountToken(effective.description) && (
              <div className="text-xs text-amber-700 mt-1" data-testid={`trust-amount-missing-${key}`}>
                This description has no {AMOUNT_TOKEN}, so the amount below will not be shown.{" "}
                <button type="button" className="underline min-h-[32px]" onClick={insertAmount} data-testid={`trust-insert-amount-${key}`}>Add it</button>
              </div>
            )}
          </div>
          {hasAmount && (
            <ThresholdField sectionKey={key} draft={draft} set={set} section={section} limits={limits}
              invalid={amountInvalid} checkoutThreshold={checkoutThreshold} effective={effective} />
          )}
          <div>
            <div className="overline mb-1">Icon</div>
            <IconPicker sectionKey={key} icons={icons} value={effective.icon} defaultIcon={section.defaults.icon}
              onPick={(icon) => set("icon", icon)} />
          </div>
        </div>
        <BadgePreview sectionKey={key} effective={effective} hasAmount={hasAmount} enabled={section.enabled} />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 mt-5">
        {dirty && <span className="text-xs text-[hsl(var(--kb-muted))]" data-testid={`trust-unsaved-${key}`}>Unsaved changes</span>}
        <button type="button" className="kb-btn-primary w-full sm:w-auto min-h-[44px]" disabled={!dirty || amountInvalid || !!busy}
          onClick={onSave} data-testid={`trust-save-${key}`}>
          {busy === "save" ? "Saving…" : "Save section"}
        </button>
      </div>
    </div>
  );
}

export default function TrustBadgesEditor({ themePreset, onSaved }) {
  const [config, setConfig] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});          // { [sectionKey]: "save" | "toggle" | "move" }
  const [loadError, setLoadError] = useState("");
  // The amount checkout really uses, when the tenant has set one. Read-only
  // here; only ever used to warn that the displayed amount differs from it.
  const [checkoutThreshold, setCheckoutThreshold] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/branding/home-trust-badges")
      .then(({ data }) => {
        if (!alive) return;
        setConfig(data);
        setDrafts(Object.fromEntries(data.sections.map((s) => [s.key, toDraft(s)])));
      })
      .catch((e) => alive && setLoadError(apiErrorMessage(e, "Could not load the store benefits")));
    api.get("/loyalty/admin/rules")
      .then(({ data }) => {
        const amount = Number(data?.free_shipping_threshold_inr);
        if (alive && data?.free_shipping_enabled && Number.isFinite(amount)) setCheckoutThreshold(amount);
      })
      .catch(() => { /* the warning is optional */ });
    return () => { alive = false; };
  }, []);

  /** PUT a partial update for one or more sections; returns true on success. */
  const send = async (keys, action, body, failMessage) => {
    setBusy((b) => ({ ...b, ...Object.fromEntries(keys.map((k) => [k, action])) }));
    try {
      const { data } = await api.put("/admin/branding/home-trust-badges", body);
      setConfig(data);
      await onSaved?.();
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e, failMessage));
      return null;
    } finally {
      setBusy((b) => Object.fromEntries(Object.entries(b).filter(([k]) => !keys.includes(k))));
    }
  };

  const save = async (section) => {
    const draft = drafts[section.key];
    const body = { title: draft.title.trim() || null, description: draft.description.trim() || null, icon: draft.icon || null };
    if ("threshold" in section.defaults) body.threshold = parseThreshold(draft.threshold, config.limits.threshold_max);
    const data = await send([section.key], "save", { [section.key]: body }, "Could not save this section");
    if (!data) return;
    // Re-seed only the card that was saved; other cards keep their own edits.
    const fresh = data.sections.find((s) => s.key === section.key);
    setDrafts((d) => ({ ...d, [section.key]: toDraft(fresh) }));
    toast.success(`${SECTION_NAMES[section.key] || fresh.title} saved`);
  };

  const toggle = async (section) => {
    const data = await send([section.key], "toggle", { [section.key]: { enabled: !section.enabled } }, "Could not update this section");
    if (data) toast.success(`${SECTION_NAMES[section.key] || section.title} is now ${section.enabled ? "hidden" : "shown"}`);
  };

  const move = async (index, step) => {
    const next = [...config.sections];
    [next[index], next[index + step]] = [next[index + step], next[index]];
    const body = Object.fromEntries(next.map((s, i) => [s.key, { order: i + 1 }]));
    await send(next.map((s) => s.key), "move", body, "Could not change the order");
  };

  if (loadError) return <div className="text-sm text-red-600" role="alert" data-testid="trust-badges-error">{loadError}</div>;
  if (!config) {
    return (
      <div className="flex items-center gap-2 text-sm text-[hsl(var(--kb-muted))]" data-testid="trust-badges-loading">
        <Loader2 size={14} className="animate-spin" /> Loading store benefits…
      </div>
    );
  }

  // Unknown theme (settings not loaded) → say nothing rather than guess.
  const stripVisible = !themePreset || TRUST_BADGE_PRESETS.includes(themePreset);
  return (
    <div data-testid="trust-badges-editor">
      <h2 className="font-display text-2xl mb-1">Store benefits</h2>
      <p className="text-xs text-[hsl(var(--kb-muted))] mb-4 leading-relaxed">
        The strip near the bottom of your home page. Edit each benefit, hide the ones you don't offer,
        and use the arrows to change their order. Leave a field blank to use its default.
      </p>
      {!stripVisible && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3 mb-4" data-testid="trust-theme-notice">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>
            Your current theme ({getPreset(themePreset).label}) does not include this strip, so shoppers will not see it yet.
            It appears on the {TRUST_BADGE_PRESETS.map((p) => getPreset(p).label).join(", ")} theme. Your settings are saved either way.
          </span>
        </div>
      )}
      <div className="space-y-6">
        {config.sections.map((section, index) => (
          <BadgeCard key={section.key} section={section} draft={drafts[section.key] || toDraft(section)}
            onDraft={(draft) => setDrafts((d) => ({ ...d, [section.key]: draft }))}
            position={index} count={config.sections.length} icons={config.icons} limits={config.limits}
            busy={busy[section.key]} checkoutThreshold={checkoutThreshold}
            onToggle={() => toggle(section)} onMove={(step) => move(index, step)} onSave={() => save(section)} />
        ))}
      </div>
    </div>
  );
}
